import React from 'react';
import {StyleSheet, Text, View} from 'react-native';
import type {Meta, StoryObj} from '@storybook/react-native-web-vite';

import {CartSummarySkeleton} from './compositions/CartSummarySkeleton';
import {DiscoverTileSkeleton} from './compositions/DiscoverTileSkeleton';
import {ProductCardSkeleton} from './compositions/ProductCardSkeleton';
import {SkeletonGroup} from './context/SkeletonGroup';

const meta = {
  title: 'Components/Loading/Skeleton Compositions',
  component: ProductCardSkeleton,
  decorators: [
    Story => (
      <View style={styles.container}>
        <Story />
      </View>
    ),
  ],
} satisfies Meta<typeof ProductCardSkeleton>;

export default meta;

type Story = StoryObj<typeof meta>;

export const CartSummary: Story = {
  render: () => (
    <View style={{maxWidth: 400}}>
      <CartSummarySkeleton />
    </View>
  ),
};

export const DiscoverTile: Story = {
  render: () => (
    <View style={{width: 180}}>
      <DiscoverTileSkeleton />
    </View>
  ),
};

export const DiscoverTileVariedHeights: Story = {
  render: () => (
    <View style={styles.grid}>
      <View style={styles.gridItem}>
        <DiscoverTileSkeleton index={0} imageHeight={180} />
      </View>
      <View style={styles.gridItem}>
        <DiscoverTileSkeleton index={1} imageHeight={140} />
      </View>
      <View style={styles.gridItem}>
        <DiscoverTileSkeleton index={2} imageHeight={220} />
      </View>
    </View>
  ),
};

export const DiscoverMasonryGridDesktop: Story = {
  render: () => {
    const heights = [180, 140, 220, 160, 200, 130, 240, 150];
    const columns = 4;
    const cellsPerCol = 3;
    return (
      <View style={[styles.masonryRow, {width: 880}]}>
        {Array.from({length: columns}).map((_, col) => (
          <View key={`col-${col}`} style={styles.masonryColumn}>
            {Array.from({length: cellsPerCol}).map((__, row) => {
              const i = col * cellsPerCol + row;
              return (
                <DiscoverTileSkeleton
                  key={`cell-${col}-${row}`}
                  index={i}
                  imageHeight={heights[i % heights.length]}
                />
              );
            })}
          </View>
        ))}
      </View>
    );
  },
};

export const DiscoverMasonryGridMobile: Story = {
  render: () => {
    const heights = [180, 140, 220, 160, 200, 130, 240, 150];
    const columns = 2;
    const cellsPerCol = 4;
    return (
      <View style={[styles.masonryRow, {width: 360}]}>
        {Array.from({length: columns}).map((_, col) => (
          <View key={`col-${col}`} style={styles.masonryColumn}>
            {Array.from({length: cellsPerCol}).map((__, row) => {
              const i = col * cellsPerCol + row;
              return (
                <DiscoverTileSkeleton
                  key={`cell-${col}-${row}`}
                  index={i}
                  imageHeight={heights[i % heights.length]}
                />
              );
            })}
          </View>
        ))}
      </View>
    );
  },
};

export const SkeletonGroupLoading: Story = {
  render: () => (
    <SkeletonGroup isLoading={true} skeleton={<ProductCardSkeleton />}>
      <View style={styles.placeholder}>
        <Text>Real product card would render here</Text>
      </View>
    </SkeletonGroup>
  ),
};

export const SkeletonGroupLoaded: Story = {
  render: () => (
    <SkeletonGroup isLoading={false} skeleton={<ProductCardSkeleton />}>
      <View style={styles.placeholder}>
        <Text>Real content is visible</Text>
      </View>
    </SkeletonGroup>
  ),
};

const styles = StyleSheet.create({
  container: {
    padding: 20,
    gap: 24,
  },
  grid: {
    flexDirection: 'row',
    gap: 12,
  },
  gridItem: {
    flex: 1,
  },
  masonryRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  masonryColumn: {
    flex: 1,
    paddingHorizontal: 4,
  },
  placeholder: {
    padding: 20,
    backgroundColor: '#e8f5e9',
    borderRadius: 8,
    alignItems: 'center',
  },
});
